"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import Script from "next/script";
import SaveMethodSurface from "@/components/payments/save-method-surface";
import { Button } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim, useUpdateClaimedPatient } from "@/hooks/patient-claim/use-patient-claim";
import { formatCardOnFilePolicy } from "@/lib/card-on-file-policy";
import { flattenSelectedServices } from "@/lib/service-addon-utils";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { createPaymentMethodConfig, savePaymentMethod } from "@/api/patient/portal-billing";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";
interface PaymentMethodStepProps {
  className?: string;
}
const PaymentMethodStep = ({
  className
}: PaymentMethodStepProps) => {
  const {
    selectedServices,
    setPaymentMethodCollected
  } = useSelfScheduleContext();
  const {
    selectedAddons
  } = useSelfScheduleStore();
  const {
    claimedPatient: patient
  } = usePatientClaim();
  const setPatient = useUpdateClaimedPatient();
  const [paymentMethodConfig, setPaymentMethodConfig] = useState<RainforestPaymentMethodConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isRequired = selectedServices?.some(service => service.creditCardRequired);
  const policyServiceIds = useMemo(() => flattenSelectedServices(selectedServices ?? [], selectedAddons).map(service_0 => service_0.id), [selectedServices, selectedAddons]);
  const {
    data: cancellationPolicy
  } = useQuery(selfScheduleQueryOptions.cancellationPolicy(policyServiceIds));
  const fetchPaymentMethodConfig = async () => {
    if (!patient?.id) return;
    setIsLoading(true);
    try {
      const config = await createPaymentMethodConfig();
      if (config) {
        setPaymentMethodConfig({
          ...config,
          paymentMethods: []
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to load payment form. Please try again.",
        variant: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (patient?.id) {
      fetchPaymentMethodConfig();
    }
  }, [patient?.id]);
  const handleSuccess = async (paymentMethodId: string) => {
    if (!patient?.id) return;
    try {
      if (!paymentMethodConfig?.paymentMethodConfigId) {
        toast({
          title: "Payment Method Error",
          description: "Card form expired. Please reload and try again.",
          variant: "error"
        });
        return;
      }
      const paymentMethod = await savePaymentMethod(paymentMethodId, paymentMethodConfig.paymentMethodConfigId);
      if (!paymentMethod) {
        toast({
          title: "Payment Method Error",
          description: "Failed to add payment method. Please try again.",
          variant: "error"
        });
        return;
      }
      toast({
        title: "Payment Method Added",
        description: "Your payment method has been saved successfully.",
        variant: "success"
      });
      setPatient(p => p ? {
        ...p,
        hasPaymentMethod: true
      } : p);
      setPaymentMethodCollected(true);
      setPaymentMethodConfig(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to save payment method. Please try again.",
        variant: "error"
      });
    }
  };
  const handleFailure = (error: string) => {
    toast({
      title: "Payment Method Error",
      description: error,
      variant: "error"
    });
  };
  const handleSkip = () => {
    if (isRequired) {
      toast({
        title: "Payment Method Required",
        description: "A payment method is required to book this appointment. Please add a payment method to continue.",
        variant: "error"
      });
      return;
    }
    setPaymentMethodCollected(true);
  };
  if (!selectedServices?.length || !patient?.id) {
    return null;
  }
  return <div data-testid="step-payment-method" className={cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className)}>
      <div className="mb-6">
        <h1 className="mb-2 text-h1 text-foreground">Add Payment Method</h1>
        <div className="space-y-2 text-body text-foreground-secondary">
          {isRequired ? <p>
              A payment method is required to book this appointment. Please add
              a credit card to continue.
            </p> : <p>
              We recommend adding a payment method to secure your appointment.
            </p>}
          {cancellationPolicy && <p>{formatCardOnFilePolicy(cancellationPolicy)}</p>}
        </div>
      </div>

      {isLoading ? <div className="py-8 text-center">
          <p className="text-muted-foreground">Loading payment form...</p>
        </div> : paymentMethodConfig ? <>
          <Script src={paymentMethodConfig.sandbox ? "https://static.rainforestpay.com/sandbox.payment.js" : "https://static.rainforestpay.com/payment.js"} strategy="afterInteractive" />
          <div className="mb-4">
            <SaveMethodSurface audience="patient" paymentMethodConfig={paymentMethodConfig} onSuccess={handleSuccess} onFailure={handleFailure} onValid={() => {}} onInvalid={() => {}} hideButton={false}
        // Card-on-file must be a storable, re-chargeable credit card.
        // Apple Pay wallet tokens and ACH aren't offered here: wallet
        // tokens can't be charged merchant-initiated later (no-show /
        // cancellation fees) and are filtered out of the provider billing
        // surfaces, so they'd satisfy the requirement at booking but be
        // unusable afterward. Matches the staff-side check-in flow.
        allowedMethods="CARD" />
          </div>
          {!isRequired && <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={handleSkip}>
                <span>Skip for now</span>
              </Button>
            </div>}
        </> : <div className="py-8 text-center">
          <p className="text-muted-foreground">
            Unable to load payment form. Please try again.
          </p>
          <Button variant="primary" className="mt-4" onClick={fetchPaymentMethodConfig}>
            <span>Retry</span>
          </Button>
        </div>}
    </div>;
};
export default PaymentMethodStep;